import { types } from "mobx-state-tree"

/**
 * Tab Item Model
 * Represents a single tab with its configuration
 */
export const TabItem = types.model("TabItem", {
  id: types.identifier,
  label: types.string,
  disabled: types.optional(types.boolean, false),
})

/**
 * TabsStore
 * MobX State Tree store for managing tabs state
 *
 * Features:
 * - Controlled selected tab state
 * - Tab selection callback support
 * - Prevents unnecessary re-renders through observer pattern
 * - Type-safe tab management
 */
export const TabsStore = types
  .model("TabsStore", {
    tabs: types.array(TabItem),
    selectedTab: types.maybe(types.string),
  })
  .views((self) => ({
    /**
     * Get the currently selected tab value
     */
    get currentTab() {
      return self.selectedTab || (self.tabs.length > 0 ? self.tabs[0].id : undefined)
    },

    /**
     * Check if a specific tab is selected
     */
    isTabSelected(tabId: string) {
      return this.currentTab === tabId
    },

    /**
     * Get tab by ID
     */
    getTabById(tabId: string) {
      return self.tabs.find((tab) => tab.id === tabId)
    },

    /**
     * Get all enabled tabs
     */
    get enabledTabs() {
      return self.tabs.filter((tab) => !tab.disabled)
    },
  }))
  .actions((self) => ({
    /**
     * Set the selected tab
     */
    setSelectedTab(tabId: string) {
      const tab = self.getTabById(tabId)
      if (tab && !tab.disabled) {
        self.selectedTab = tabId
      }
    },

    /**
     * Add a new tab
     */
    addTab(tab: { id: string; label: string; disabled?: boolean }) {
      self.tabs.push(TabItem.create(tab))
    },

    /**
     * Set all tabs at once
     */
    setTabs(tabs: Array<{ id: string; label: string; disabled?: boolean }>) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.tabs.replace(tabs.map((tab) => TabItem.create(tab)) as any)
    },

    /**
     * Remove a tab by ID
     */
    removeTab(tabId: string) {
      const index = self.tabs.findIndex((tab) => tab.id === tabId)
      if (index !== -1) {
        self.tabs.splice(index, 1)
      }
    },

    /**
     * Toggle tab disabled state
     */
    toggleTabDisabled(tabId: string) {
      const tab = self.getTabById(tabId)
      if (tab) {
        tab.disabled = !tab.disabled
      }
    },

    /**
     * Clear all tabs
     */
    clearTabs() {
      self.tabs.clear()
      self.selectedTab = undefined
    },
  }))

export type TabsStoreType = typeof TabsStore.Type
export type TabItemType = typeof TabItem.Type
